import { useEffect, useState } from 'react';
import { useCart } from './useCart.js';
import { useLang, LANGUAGE_OPTIONS } from './useLanguage.js';
import {
    buildWaitlistPrefilledCart,
    clearWaitlistPrefillFromUrl,
    getWaitlistPrefillFromUrl,
} from '../waitlist/waitlistPrefill.js';
import type { TicketOptionsSuccessResponseResponseTicketOptionsItem } from '../../openapi/model/ticketOptionsSuccessResponseResponseTicketOptionsItem';
import type { TicketTypesSuccessResponseResponseTicketTypesItem } from '../../openapi/model/ticketTypesSuccessResponseResponseTicketTypesItem';

type WaitlistState = {
    isActive: boolean;
    forceSoldOutStatus: boolean;
};

export function useWaitlist({
    ticketTypes,
    ticketOptions,
    setStep,
}: {
    ticketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[];
    ticketOptions: TicketOptionsSuccessResponseResponseTicketOptionsItem[];
    setStep: (step: number) => void;
}) {
    const { setCart, setSeats, setTimeslot, setGenericBookingFields, clearCoupon } = useCart();
    const { setLanguage } = useLang();

    const [waitlistState, setWaitlistState] = useState<WaitlistState>({
        isActive: false,
        forceSoldOutStatus: false,
    });

    useEffect(() => {
        if (ticketTypes.length === 0 || ticketOptions.length === 0) {
            return;
        }

        const prefill = getWaitlistPrefillFromUrl();
        if (!prefill) {
            return;
        }

        const prefilledCart = buildWaitlistPrefilledCart(prefill, ticketTypes, ticketOptions);

        if (prefilledCart.length === 0) {
            clearWaitlistPrefillFromUrl();
            return;
        }

        setTimeslot({
            id: prefill.timeslotId,
            startTime: prefill.timeslotStartTime,
        });
        if (prefill.language && LANGUAGE_OPTIONS[prefill.language]) {
            setLanguage(prefill.language);
        }
        if (prefill.genericBookingFields) {
            setGenericBookingFields(prefill.genericBookingFields);
        }
        setCart(prefilledCart);
        setSeats([]);
        setStep(2);
        clearWaitlistPrefillFromUrl();
    }, [
        setCart,
        setGenericBookingFields,
        setLanguage,
        setSeats,
        setStep,
        setTimeslot,
        ticketOptions,
        ticketTypes,
    ]);

    const openWaitlist = (forceSoldOutStatus = false) => {
        setWaitlistState({
            isActive: true,
            forceSoldOutStatus,
        });
    };

    const closeWaitlist = () => {
        setWaitlistState({
            isActive: false,
            forceSoldOutStatus: false,
        });
    };

    const resetBookingState = () => {
        setCart([]);
        setSeats([]);
        setTimeslot(null);
        setGenericBookingFields({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
        });
        clearCoupon();
    };

    return {
        waitlistState,
        openWaitlist,
        closeWaitlist,
        resetBookingState,
    };
}
